import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatDateTime } from '../utils/format';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import ExpandAllHeader from '../components/ExpandAllHeader';
import AutoRefreshControls from '../components/AutoRefreshControls';

function NowGoalOUDetails({ token, backendUrl, dateRange, setDateRange, error, setError }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedGoals, setExpandedGoals] = useState(new Set());

  const pagination = usePagination('nowGoalOU.pageSize', 50);
  const { sortableHeader, sortData } = useTableSort('created_at', 'desc');
  const autoRefresh = useAutoRefresh((signal) => fetchData(signal), { defaultInterval: 60, defaultEnabled: true });

  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    setExpandedRows(new Set());
    setExpandedGoals(new Set());
    try {
      const skip = (pagination.currentPage - 1) * pagination.pageSize;
      const headers = { Authorization: `Bearer ${token}` };
      const [dataRes, countRes] = await Promise.all([
        axios.get(`${backendUrl}/now_goal/ou_details`, {
          params: { start_date: dateRange.start_date, end_date: dateRange.end_date, skip, limit: pagination.pageSize },
          headers,
          signal,
        }),
        axios.get(`${backendUrl}/now_goal/ou_details/count`, {
          params: { start_date: dateRange.start_date, end_date: dateRange.end_date },
          headers,
          signal,
        }),
      ]);
      if (signal?.aborted) return;
      setData(Array.isArray(dataRes.data?.data) ? dataRes.data.data : []);
      pagination.setTotalCount(countRes.data?.total_count ?? 0);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setError(err.response?.data?.detail || err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [backendUrl, token, dateRange.start_date, dateRange.end_date, pagination, setError]);

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [token, fetchData]);

  const handleApply = () => {
    pagination.resetPage();
    setError(null);
  };

  const toggleRow = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGoal = (key) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredAndSorted = useMemo(() => {
    let result = data;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((item) =>
        String(item.league_name ?? '').toLowerCase().includes(q) ||
        String(item.home_team ?? '').toLowerCase().includes(q) ||
        String(item.away_team ?? '').toLowerCase().includes(q) ||
        String(item.url ?? '').toLowerCase().includes(q)
      );
    }
    return sortData(result, ['created_at']);
  }, [data, searchQuery, sortData]);

  const allRowsExpanded = expandedRows.size === filteredAndSorted.length && filteredAndSorted.length > 0;

  const toggleExpandAllRows = () => {
    if (allRowsExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(filteredAndSorted.map((_, idx) => `row-${idx}`)));
    }
  };

  const companyBadge = (company) => {
    const map = {
      bet365: 'bg-success',
      sbobet: 'bg-primary',
      m88: 'bg-warning text-dark',
    };
    return <span className={`badge ${map[company] || 'bg-secondary'} me-1`}>{company}</span>;
  };

  // 取得比分顯示：三態（比賽中 / 等待資料 / 資料缺失 / 已完成）
  const getScoreDisplay = (doc) => {
    const fs = String(doc.full_score ?? '').trim();
    if (fs && fs !== '-') {
      const parts = fs.split('-').map((s) => s.trim());
      return <span className="badge bg-dark">{parts[0]} : {parts[1]}</span>;
    }
    const hoursElapsed = (Date.now() - new Date(doc.created_at).getTime()) / (1000 * 60 * 60);
    if (hoursElapsed < 3) {
      return <span className="badge bg-info text-dark">比賽中</span>;
    }
    if (hoursElapsed < 6) {
      return <span className="badge bg-warning text-dark">等待資料</span>;
    }
    return <span className="badge bg-secondary">資料缺失</span>;
  };

  // 計算每筆 doc 的公司數和 goals 數
  const getDocSummary = (doc) => {
    const companies = (doc.company_data || []).map(c => c.company);
    const totalGoals = (doc.company_data || []).reduce((sum, c) => sum + (c.goals_data || []).length, 0);
    return { companies, totalGoals };
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('nowGoalOU.title')}</h2>
        <AutoRefreshControls {...autoRefresh} />
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label">{t('common.search')}</label>
          <input
            className="form-control"
            placeholder={t('nowGoalOU.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label mb-0 small">{t('common.startDate')}</label>
          <input type="date" className="form-control" value={dateRange.start_date} onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))} />
        </div>
        <div className="col-auto">
          <label className="form-label mb-0 small">{t('common.endDate')}</label>
          <input type="date" className="form-control" value={dateRange.end_date} onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))} />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={handleApply}>{t('common.apply')}</button>
        </div>
      </div>

      <div className="text-muted small mb-2">
        {t('common.dateRange')}: {dateRange.start_date} {t('common.to')} {dateRange.end_date} | {t('common.totalCount')}: {pagination.totalCount}
      </div>

      <PaginationControls {...pagination} />

      <ErrorLoading error={error} loading={loading} />

      {!loading && (
        <div className="table-responsive">
          <table className="table table-bordered table-sm">
            <thead className="table-secondary">
              <tr>
                <ExpandAllHeader allExpanded={allRowsExpanded} onToggle={toggleExpandAllRows} />
                {sortableHeader(t('nowGoalOU.league'), 'league_name')}
                {sortableHeader(t('nowGoalOU.homeTeam'), 'home_team')}
                {sortableHeader(t('nowGoalOU.awayTeam'), 'away_team')}
                <th>{t('nowGoalOU.company')}</th>
                <th>Score</th>
                <th>Goals</th>
                {sortableHeader(t('common.createdAt'), 'created_at')}
                <th style={{ width: '40px' }}>URL</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.length === 0 && (
                <tr><td colSpan={9} className="text-center text-muted">{t('common.noData')}</td></tr>
              )}
              {filteredAndSorted.map((doc, idx) => {
                const rowKey = `row-${idx}`;
                const isExpanded = expandedRows.has(rowKey);
                const { companies, totalGoals } = getDocSummary(doc);
                return (
                  <>
                    {/* 主列：一筆資料一行 */}
                    <tr key={rowKey} style={{ cursor: 'pointer' }} onClick={() => toggleRow(rowKey)}
                        className={isExpanded ? 'table-active' : ''}>
                      <td className="text-center">
                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </td>
                      <td>{doc.league_name}</td>
                      <td>{doc.home_team}</td>
                      <td>{doc.away_team}</td>
                      <td>{companies.map(c => companyBadge(c))}</td>
                      <td className="text-center">{getScoreDisplay(doc)}</td>
                      <td><span className="badge bg-secondary">{totalGoals} lines</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(doc.created_at)}</td>
                      <td className="text-center">
                        {doc.url && (
                          <a href={doc.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={doc.url} aria-label={doc.url}>
                            <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                          </a>
                        )}
                      </td>
                    </tr>

                    {/* 展開：公司 + Goals 明細 */}
                    {isExpanded && (doc.company_data || []).map((comp) => (
                      <tr key={`${rowKey}-${comp.company}`} style={{ '--bs-table-bg': 'var(--bs-tertiary-bg)' }}>
                        <td></td>
                        <td colSpan={8}>
                          <div className="mb-2">
                            <strong>{companyBadge(comp.company)}</strong>
                            <span className="text-body-secondary ms-2">{(comp.goals_data || []).length} goals lines</span>
                          </div>
                          <table className="table table-sm table-bordered mb-0">
                            <thead>
                              <tr className="table-secondary">
                                <th style={{ width: '30px' }}></th>
                                <th>{t('nowGoalOU.goals')}</th>
                                <th>{t('nowGoalOU.htCount')}</th>
                                <th>(Under)Max</th>
                                <th>(Under)Min</th>
                                <th>{t('nowGoalOU.gap')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(comp.goals_data || []).map((g, gi) => {
                                const goalKey = `${rowKey}-${comp.company}-${gi}`;
                                const isGoalExpanded = expandedGoals.has(goalKey);
                                const underEntries = (g.data || []).filter(r => r.under != null);
                                const underMax = underEntries.length > 0 ? Math.max(...underEntries.map(r => r.under)) : null;
                                const underMin = underEntries.length > 0 ? Math.min(...underEntries.map(r => r.under)) : null;
                                const underGap = underMax != null ? (underMax - underMin).toFixed(2) : '-';
                                const maxTime = underMax != null
                                  ? underEntries.filter(r => r.under === underMax).sort((a, b) => (b.update_time || '').localeCompare(a.update_time || ''))[0]?.time || ''
                                  : '';
                                const minTime = underMin != null
                                  ? underEntries.filter(r => r.under === underMin).sort((a, b) => (b.update_time || '').localeCompare(a.update_time || ''))[0]?.time || ''
                                  : '';
                                return (
                                  <>
                                    <tr key={goalKey} style={{ cursor: 'pointer' }} onClick={() => toggleGoal(goalKey)}>
                                      <td className="text-center">
                                        <i className={`bi bi-chevron-${isGoalExpanded ? 'up' : 'down'} text-muted`}></i>
                                      </td>
                                      <td><strong>{g.goals}</strong></td>
                                      <td>{g.count}</td>
                                      <td>{underMax != null ? <>{underMax} <span className="text-muted">({maxTime})</span></> : '-'}</td>
                                      <td>{underMin != null ? <>{underMin} <span className="text-muted">({minTime})</span></> : '-'}</td>
                                      <td>{underGap}</td>
                                    </tr>
                                    {isGoalExpanded && (g.data || []).length > 0 && (
                                      <tr key={`${goalKey}-detail`}>
                                        <td></td>
                                        <td colSpan={5}>
                                          <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                                            <thead>
                                              <tr className="table-secondary">
                                                <th>Time</th>
                                                <th>Score</th>
                                                <th>Over</th>
                                                <th>Goals</th>
                                                <th>Under</th>
                                                <th>Update</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(g.data || []).slice().sort((a, b) => (b.update_time || '').localeCompare(a.update_time || '')).map((r, ri) => (
                                                <tr key={ri}>
                                                  <td>{r.time}</td>
                                                  <td>{r.score}</td>
                                                  <td className="text-danger">{r.over}</td>
                                                  <td><strong>{r.goals}</strong></td>
                                                  <td className="text-success">{r.under}</td>
                                                  <td>{r.update_time}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NowGoalOUDetails;
